import {
  ComponentPropsWithoutRef,
  ElementType,
  PropsWithChildren,
} from 'react';

import { CircleLoader } from '../CircleLoader';

import clsx from 'clsx';

export enum ButtonAction {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export enum ButtonSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

type AsProp<E extends ElementType> = {
  as?: E;
};

type ComponentProps<E extends ElementType> = PropsWithChildren<
  ComponentPropsWithoutRef<E> & AsProp<E>
>;

type ButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  action?: ButtonAction;
  size?: ButtonSize;
  isLoading?: boolean;
};

const defaultElement = 'button';

export function Button<E extends ElementType = typeof defaultElement>({
  as,
  action = ButtonAction.PRIMARY,
  size = ButtonSize.MEDIUM,
  isLoading,
  children,
  disabled,
  className,
  ...otherProps
}: ButtonProps & ComponentProps<E>) {
  const baseClasses =
    'inline-flex items-center justify-center rounded-full font-medium border group text-center cursor-pointer disabled:cursor-default';
  const Component = as ?? defaultElement;

  const getSizeClasses = () => {
    switch (size) {
      case ButtonSize.SMALL:
        return 'text-sm leading-none py-1 px-5';
      case ButtonSize.LARGE:
        return 'text-lg py-2 px-5';
      default:
        return 'text-base py-2 px-5';
    }
  };

  const getActionClasses = () => {
    switch (action) {
      case ButtonAction.SECONDARY:
        return 'bg-white border-teal-40 text-teal-40 hover:bg-teal-40 hover:text-white disabled:pointer-events-none disabled:text-grey-60 disabled:border-grey-60';
      case ButtonAction.TERTIARY:
        return 'bg-white border-[2px] border-[#013E3F1A] text-teal-40 hover:bg-[#013E3F0A]';
      default:
        return 'bg-teal-40 border-teal-40 text-white';
    }
  };

  const getCircleLoaderClasses = () => {
    switch (action) {
      case ButtonAction.SECONDARY:
        return 'fill-teal-40 group-hover:fill-white';
      default:
        return 'fill-white';
    }
  };

  return (
    <Component
      className={clsx(
        baseClasses,
        getSizeClasses(),
        getActionClasses(),
        className
      )}
      disabled={disabled}
      {...otherProps}
    >
      {isLoading && (
        <CircleLoader
          className={clsx(
            'mr-2',
            disabled && '!fill-grey-60',
            getCircleLoaderClasses()
          )}
        />
      )}
      {children}
    </Component>
  );
}
